/**
 * What the AI models believe about this brand (SYR-111, phase 3).
 *
 * The same Brand Equity statements the brand's own customers answer, put to the
 * models behind ChatGPT, Claude and Gemini as a shopper in the brand's market
 * with live web search on. The awareness gate leads, because a brand that is
 * never recommended never has its drivers evaluated at all.
 *
 * The API answers 200 with a reason when the feature is gated, so every "you
 * cannot see this" case below is a state to render rather than an error.
 */

import React from 'react';
import { Alert, Button, Card, Empty, Progress, Skeleton, Space, Tag, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useNavReturn } from '../nav/NavReturnContext';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { MarketerPageHeader } from './MarketerPageHeader';
import { THEME } from '../config/theme';
import {
  useAddMarketerActionPlanPlayMutation,
  useGetBrandModelBeliefsQuery,
  useRemoveMarketerActionPlanPlayMutation,
  useRequestBrandModelBeliefsMutation,
  useResynthesiseBrandModelBeliefsMutation,
} from '../../../services/nestjs-base-api';
import { RecordFixEntry } from '../components/modelBeliefs/RecordFixes';
import { ModelBeliefsRead } from '../components/modelBeliefs/ModelBeliefsRead';

const { Text, Paragraph } = Typography;

const IN_FLIGHT = ['QUEUED', 'SAMPLING', 'GATING', 'AGGREGATING', 'SYNTHESISING'];

export const ModelBeliefsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const { activeBrand } = useMarketerScope();
  const { navigateWithReturn } = useNavReturn();
  const marketerRoot = useMarketerRoot();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const brandId = activeBrand?.id ?? 0;
  // ?run=42 asks the API for that run instead of the latest customer read.
  // The API only honours it for a platform admin or an impersonation
  // session, so for a customer the parameter is inert.
  const [searchParams, setSearchParams] = useSearchParams();
  const runParam = Number(searchParams.get('run'));
  const pinnedRunId = Number.isInteger(runParam) && runParam > 0 ? runParam : undefined;
  // ?survey=0 shows the read as a brand with no survey sees it. Admins and
  // impersonation only; the API ignores it for anyone else.
  const hideSurvey = searchParams.get('survey') === '0';

  const { data, isLoading, refetch } = useGetBrandModelBeliefsQuery(
    { jwtToken, organisationId, brandId, runId: pinnedRunId, hideSurvey },
    { skip: !jwtToken || !organisationId || !brandId },
  );
  const [requestRead, requestState] = useRequestBrandModelBeliefsMutation();
  const [resynthesise, resynthesiseState] = useResynthesiseBrandModelBeliefsMutation();
  const [addPlay] = useAddMarketerActionPlanPlayMutation();
  const [removePlay] = useRemoveMarketerActionPlanPlayMutation();
  const [busyPlayId, setBusyPlayId] = React.useState<string | null>(null);

  // The card is stored as the play's snapshot, so the Action Plan keeps what
  // the read said at the time even after the next read rewords it.
  const toggleRecordFix = async (entry: RecordFixEntry) => {
    setBusyPlayId(entry.playId);
    try {
      if (entry.added) {
        await removePlay({ jwtToken, organisationId, brandId, playId: entry.playId }).unwrap();
      } else {
        await addPlay({
          jwtToken,
          organisationId,
          brandId,
          playId: entry.playId,
          snapshot: entry.card,
        }).unwrap();
      }
      refetch();
    } finally {
      setBusyPlayId(null);
    }
  };

  const title = intl.formatMessage({
    id: 'marketerView.modelBeliefs.title',
    defaultMessage: 'The Machines',
  });
  const subtitle = intl.formatMessage({
    id: 'marketerView.modelBeliefs.subtitle',
    defaultMessage:
      'When a shopper asks ChatGPT, Claude or Gemini what to buy, the answer comes from what those models believe about your brand. We asked them. This is what they said.',
  });

  if (isLoading || !data) {
    return (
      <div>
        <MarketerPageHeader title={title} subtitle={subtitle} />
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  // Not on this plan. The API still says whether the feature is live at all, so
  // this is the upgrade path rather than a dead end.
  if (!data.entitled) {
    return (
      <div>
        <MarketerPageHeader title={title} subtitle={subtitle} />
        <Card>
          <Space direction="vertical" size={12}>
            <Text strong style={{ fontSize: 16 }}>
              {intl.formatMessage({
                id: 'marketerView.modelBeliefs.locked.title',
                defaultMessage: 'Included on a higher plan',
              })}
            </Text>
            <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, maxWidth: '60ch' }}>
              {intl.formatMessage({
                id: 'marketerView.modelBeliefs.locked.body',
                defaultMessage:
                  'Shoppers ask an assistant before they ask you. This read measures what those assistants believe about your brand, using the same statements your customers answer, and shows where the public record is costing you recommendations.',
              })}
            </Paragraph>
            <Button
              type="primary"
              // Flat, never a gradient (marketer-view button standard).
              style={{ background: THEME.primary, borderColor: THEME.primary }}
              href="/dashboard/organisation?tab=billing#upgrade"
            >
              {intl.formatMessage({
                id: 'marketerView.modelBeliefs.locked.cta',
                defaultMessage: 'See plans',
              })}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // Entitled, but the feature is dark for customers. Only a platform admin or
  // an impersonation session ever reaches this page in that state.
  if (!data.enabled) {
    return (
      <div>
        <MarketerPageHeader title={title} subtitle={subtitle} />
        <Empty description="This feature is not live yet." />
      </div>
    );
  }

  const latest = data.latest ?? null;
  const inFlight = data.inFlight && IN_FLIGHT.includes(data.inFlight.status) ? data.inFlight : null;

  return (
    <div>
      <MarketerPageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <Space wrap>
            {data.previewOnly && (
              <Tag color="orange">Not yet live for customers</Tag>
            )}
            {data.pinnedRunId && (
              <Tag color="purple" style={{ whiteSpace: 'normal' }}>
                Showing run #{data.pinnedRunId}, not the latest customer read
              </Tag>
            )}
            {data.surveyHidden && (
              <Tag color="purple" style={{ whiteSpace: 'normal' }}>
                Customer data hidden: the read as a brand with no survey sees it
              </Tag>
            )}
            {/* Admin and impersonation only. Most brands run this read before
                they have a survey, so the page and the written read must be
                checked in that state, on a brand that happens to have one. */}
            {data.canResynthesise && (
              <Space size={6} wrap>
                <Button
                  size="small"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (hideSurvey) next.delete('survey');
                    else next.set('survey', '0');
                    setSearchParams(next);
                  }}
                >
                  {hideSurvey ? 'Show customer data' : 'Hide customer data'}
                </Button>
                <Button
                  size="small"
                  loading={resynthesiseState.isLoading}
                  onClick={async () => {
                    await resynthesise({
                      jwtToken,
                      organisationId,
                      brandId,
                      runId: data.pinnedRunId ?? undefined,
                      withoutSurvey: hideSurvey,
                    }).unwrap();
                    refetch();
                  }}
                >
                  {hideSurvey ? 'Re-synthesise without customer data' : 'Re-synthesise'}
                </Button>
              </Space>
            )}
            {latest?.completedAt && (
              <Text style={{ color: THEME.textSecondary, fontSize: 13 }}>
                Last read {new Date(latest.completedAt).toLocaleDateString()}
              </Text>
            )}
            {/* Hidden, not disabled, while manual reads are off: a greyed
                button promises something the customer cannot have. Reads
                arrive on the schedule; the API refuses a request either way. */}
            {data.manualReadEnabled !== false && (
              <Button
                type="primary"
                // The inline colour is applied ONLY while the button is live:
                // painting a disabled button in the primary colour makes it look
                // clickable and the cooldown look like a bug.
                style={
                  !!inFlight || data.canRequest === false
                    ? undefined
                    : { background: THEME.primary, borderColor: THEME.primary }
                }
                disabled={!!inFlight || data.canRequest === false}
                loading={requestState.isLoading}
                onClick={async () => {
                  await requestRead({ jwtToken, organisationId, brandId }).unwrap();
                  refetch();
                }}
              >
                {latest ? 'Read again' : 'Run the first read'}
              </Button>
            )}
          </Space>
        }
      />

      {/* A read takes a while and costs real money, so the page says what is
          happening rather than leaving a spinner to imply it is nearly done. */}
      {inFlight && (
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text strong>A read is running</Text>
            <Progress
              percent={
                inFlight.callsBudget > 0
                  ? Math.min(Math.round((inFlight.callsMade / inFlight.callsBudget) * 100), 99)
                  : 0
              }
              status="active"
              strokeColor={THEME.primary}
            />
            <Text style={{ color: THEME.textSecondary, fontSize: 13 }}>
              {inFlight.callsMade} of {inFlight.callsBudget} questions asked
              {inFlight.stage ? ` · ${inFlight.stage}` : ''}. Every statement is asked many times
              of every model, so this takes a while. You can leave the page.
            </Text>
          </Space>
        </Card>
      )}

      {data.manualReadEnabled !== false && data.canRequest === false && data.nextRequestAt && !inFlight && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The next read is available later"
          description={`Reads are spaced out because the public record does not move in a day: a second read now would measure noise. Available from ${new Date(
            data.nextRequestAt,
          ).toLocaleDateString()}.`}
        />
      )}

      {!latest && !inFlight && (
        <Empty
          description={
            data.config?.categoryLabel
              ? 'No read yet. Run the first one to see what the models believe.'
              : 'This brand needs its category set before a read can run. Ask your BrandScience contact to finish the setup.'
          }
        />
      )}

      {latest && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <ModelBeliefsRead
            latest={latest}
            history={(data as any).history}
            brandName={activeBrand?.name ?? 'Your brand'}
            onSendSurvey={() =>
              navigateWithReturn(
                activeBrand ? `${marketerRoot}/${activeBrand.hash}/admin/surveys` : '/dashboard/surveys',
                title,
              )
            }
            onToggleRecordFix={toggleRecordFix}
            busyPlayId={busyPlayId}
          />
        </Space>
      )}
    </div>
  );
};

export default ModelBeliefsPage;
